import { Component } from '@theme/component';
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Colorway iframe host.
 *
 * Hosts the remote Sewfinity Colorway app (apps.sewfinity.com/colorway) full-bleed, feeds it the
 * cart token (from the `cart` cookie) and an optional `share` param, and — because the app mutates
 * the cart out-of-band and only `postMessage`s the parent — refreshes the storefront cart on that
 * message by re-fetching `/cart.js` and dispatching a `CartLinesUpdateEvent`
 * (`StandardEvents.cartLinesUpdate`).
 *
 * Behavior reference (do not port verbatim): the Dawn theme app extension
 * `colorway-container.js`. That version used Dawn's `publish(PUB_SUB_EVENTS.cartUpdate, …)`
 * pub/sub; here we dispatch the standard cart event instead so Horizon's cart UI (bubble/drawer)
 * refreshes.
 *
 * Note: this originally dispatched `CartUpdateEvent` from `@theme/events`. That export was removed
 * by the upstream Horizon sync in favour of `@shopify/events`; the stale import was a module-level
 * link error, so `customElements.define` never ran and the iframe `src` was never assigned.
 *
 * See docs/plans/0005-colorway.md (Phases A & B).
 */

const ALLOWED_ORIGINS = [
  'https://apps.sewfinity.com',
  'https://qa-apps.sewfinity.com',
  'https://dev-apps.sewfinity.com',
];

const RESIZE_DEBOUNCE_MS = 300;

class ColorwayComponent extends Component {
  #abortController = new AbortController();
  #debug = false;
  #resizeTimeout = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#debug = this.dataset.isDebug === 'true';
    const { signal } = this.#abortController;

    window.addEventListener('message', this.#onAppMessage, { signal });
    window.addEventListener('resize', this.#onResize, { signal });

    this.#initSrc();
    this.#applyHeight();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    clearTimeout(this.#resizeTimeout);
  }

  /**
   * Reads the `cart` cookie, then assigns the iframe `src` with `cart`/`share` params. The cookie
   * can lag a first-visit page load, so retry with exponential backoff (200/400/800 ms, 3 attempts)
   * before giving up and loading the app without a cart token.
   */
  async #initSrc() {
    let cartId = null;
    try {
      cartId = await this.#getCartId();
    } catch {
      cartId = null;
    }
    this.#applySrc(cartId);
    this.#applyHeight();
  }

  /**
   * @returns {Promise<string | null>} the `cart` cookie value, or null after retries fail.
   */
  #getCartId() {
    return new Promise((resolve) => {
      const maxRetries = 3;
      const baseDelay = 200;
      let attempts = 0;

      const attempt = () => {
        const cartId = this.#readCookie('cart');
        if (cartId) return resolve(cartId);
        if (attempts >= maxRetries) return resolve(null);

        const delay = baseDelay * Math.pow(2, attempts); // 200, 400, 800
        attempts += 1;
        setTimeout(attempt, delay);
      };

      attempt();
    });
  }

  /**
   * @param {string} name
   * @returns {string | null}
   */
  #readCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift() ?? null;
    return null;
  }

  /**
   * @returns {string | null} the trimmed `share` query param from the current page URL.
   */
  #getShareParam() {
    try {
      const raw = new URLSearchParams(location.search).get('share');
      const trimmed = typeof raw === 'string' ? raw.trim() : '';
      return trimmed || null;
    } catch {
      return null;
    }
  }

  /**
   * @param {string | null} cartId
   * @returns {string} the iframe source URL with `cart`/`share` params applied.
   */
  #buildSrc(cartId) {
    const base = this.dataset.iframeSrc;
    if (!base) return '';

    const share = this.#getShareParam();
    try {
      const url = new URL(base, location.href);
      if (cartId) url.searchParams.set('cart', String(cartId));
      if (share) url.searchParams.set('share', share);
      return url.toString();
    } catch {
      let src = base;
      if (cartId) src += (src.includes('?') ? '&' : '?') + 'cart=' + encodeURIComponent(String(cartId));
      if (share) src += (src.includes('?') ? '&' : '?') + 'share=' + encodeURIComponent(share);
      return src;
    }
  }

  /**
   * @param {string | null} cartId
   */
  #applySrc(cartId) {
    const iframe = this.querySelector('iframe');
    if (iframe) iframe.src = this.#buildSrc(cartId);
  }

  #headerEl() {
    return document.getElementById('header-component');
  }

  /**
   * Sizes the iframe (and host) to fill the viewport below the header. The colorway layout omits the
   * footer and hides the announcement bar, so header height is the only offset needed.
   */
  #applyHeight() {
    const iframe = this.querySelector('iframe');
    if (!iframe) return;

    const header = this.#headerEl();
    const headerHeight = header instanceof HTMLElement ? header.offsetHeight : 0;
    const height = `${window.innerHeight - headerHeight}px`;

    iframe.style.height = height;
    this.style.height = height;
    this.style.display = 'block';
  }

  #onResize = () => {
    clearTimeout(this.#resizeTimeout);
    this.#resizeTimeout = window.setTimeout(() => this.#applyHeight(), RESIZE_DEBOUNCE_MS);
  };

  /**
   * The remote app mutates the cart itself, then posts `{ source: 'cart-updated' }` to the parent.
   * Re-fetch the cart and dispatch a `CartLinesUpdateEvent` so Horizon's cart UI refreshes.
   *
   * Unlike the theme's own cart surfaces we can't dispatch optimistically and resolve later: the
   * mutation already happened remotely, so we only learn what changed by re-fetching. Hence
   * fetch-then-dispatch with an already-settled promise.
   *
   * `action: 'update'` (not `'add'`) is deliberate — `cart-drawer` auto-opens only on `'add'`, and a
   * drawer sliding over the Colorway app would fight the iframe's own UX. Dawn only refreshed the
   * bubble here too.
   *
   * @param {MessageEvent} event
   */
  #onAppMessage = async (event) => {
    if (event?.data?.source !== 'cart-updated') return;
    if (!this.#debug && !ALLOWED_ORIGINS.includes(event.origin)) return;

    let cart;
    try {
      cart = await fetch(`${Theme.routes.cart_url}.js`, { cache: 'no-store' }).then((r) => r.json());
    } catch {
      return;
    }

    const deferred = CartLinesUpdateEvent.createPromise();

    // We can't know which lines the app touched, so report the cart's current lines. No consumer in
    // this theme reads `event.lines` — they all await `event.promise` — but the payload stays valid.
    const lines = Array.isArray(cart.items)
      ? cart.items.map((/** @type {any} */ item) => ({
          id: String(item.key ?? item.id),
          quantity: item.quantity,
        }))
      : [];

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines,
        promise: deferred.promise,
      })
    );

    // `cart-icon` reads `cart.totalQuantity`, falling back to `detail.itemCount`; `sections` is
    // omitted so `component-cart-items` re-renders itself via the section renderer.
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        itemCount: cart.item_count,
        source: 'colorway',
        didError: false,
      },
    });
  };
}

if (!customElements.get('colorway-component')) {
  customElements.define('colorway-component', ColorwayComponent);
}
